/**
 * @file carousel-item.tsx
 * Single Carousel slide wrapper supporting ARIA roles, dynamic clip-path masks,
 * inner parallax content translation, and MD3 touch ripples.
 *
 * @see docs/m3/carousel/Carousel_Specs_Material_Design_3.md
 * @see docs/m3/carousel/Carousel_Accessibility_Material_Design_3.md
 */

import * as React from "react";
import { cn } from "../../lib/utils";
import { Ripple, useRippleState } from "../ripple";
import type { CarouselLayout } from "./carousel.types";

export interface CarouselItemProps
	extends React.HTMLAttributes<HTMLDivElement> {
	index: number;
	totalItems: number;
	layout?: CarouselLayout;
	width?: number;
	minContentWidth?: number;
	maskClipPercent?: number;
	parallaxOffset?: number;
	isFocused?: boolean;
	isScrolling?: boolean;
	disabled?: boolean;
	children: React.ReactNode;
}

export const CarouselItem = React.forwardRef<HTMLDivElement, CarouselItemProps>(
	function CarouselItem(
		{
			index,
			totalItems,
			layout = "multi-browse",
			width,
			minContentWidth,
			maskClipPercent = 0,
			parallaxOffset = 0,
			isFocused = false,
			isScrolling = false,
			disabled = false,
			className,
			style,
			children,
			onClick,
			onPointerDown,
			...props
		},
		ref,
	) {
		const {
			ripples,
			onPointerDown: handleRipplePointerDown,
			removeRipple,
		} = useRippleState();

		const itemNumber = index + 1;
		const ariaLabel =
			props["aria-label"] || `Item ${itemNumber} of ${totalItems}`;

		const isFullScreen = layout === "full-screen";
		const isCenterHero = layout === "center-aligned-hero";

		const combinedStyle: React.CSSProperties = {
			width: isFullScreen ? "100%" : width ? `${width}px` : undefined,
			height: isFullScreen ? "100%" : undefined,
			flexShrink: 0,
			...style,
		};

		const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
			if (!disabled) {
				handleRipplePointerDown(e);
			}
			onPointerDown?.(e);
		};

		const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
			if (!disabled && (e.key === "Enter" || e.key === " ")) {
				e.preventDefault();
				onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
			}
			props.onKeyDown?.(e);
		};

		return (
			// biome-ignore lint/a11y/useSemanticElements: ARIA carousel pattern requires role="group" for slide items
			<div
				ref={ref}
				role="group"
				aria-roledescription="slide"
				aria-label={ariaLabel}
				tabIndex={disabled ? -1 : isFocused ? 0 : -1}
				className={cn(
					// Base item styles: overflow-hidden + rounded 28px acts as MD3 keyline mask
					"relative flex flex-col overflow-hidden rounded-[28px] cursor-pointer select-none box-border outline-none shrink-0",
					// GPU Composition & Layout Containment
					"will-change-[transform,clip-path] contain-[layout_paint_style] [user-select:none] [-webkit-user-drag:none] [&_img]:pointer-events-none [&_img]:select-none [&_img]:[-webkit-user-drag:none]",
					// Scroll snap: center-aligned hero snaps to center, all others snap start
					isCenterHero ? "snap-center" : "snap-start",
					// Full-screen: no rounding, fill height
					isFullScreen && "rounded-none",
					// State layers via ::after pseudo-element
					"after:absolute after:inset-0 after:rounded-[inherit] after:bg-black after:opacity-0 after:pointer-events-none after:z-2 after:transition-opacity after:duration-150 after:motion-reduce:transition-none",
					"hover:after:opacity-[0.08]",
					"active:after:opacity-[0.10]",
					// Focus ring
					"focus-visible:outline-[3px] focus-visible:outline-(--md-sys-color-secondary,#625b71) focus-visible:outline-offset-2 focus-visible:z-3",
					// Disabled
					disabled && "opacity-[0.38] pointer-events-none",
					className,
				)}
				style={combinedStyle}
				onPointerDown={handlePointerDown}
				onKeyDown={handleKeyDown}
				onClick={disabled ? undefined : onClick}
				{...props}
			>
				{/* Parallax inner content container maintained at full largeWidth */}
				<div
					className="w-full flex-1 flex flex-col will-change-transform select-none"
					style={{
						width: isFullScreen
							? "100%"
							: minContentWidth
								? `${minContentWidth}px`
								: "100%",
						minWidth: isFullScreen
							? "100%"
							: minContentWidth
								? `${minContentWidth}px`
								: undefined,
						transform:
							parallaxOffset !== 0
								? `translateX(${parallaxOffset}px)`
								: undefined,
					}}
				>
					{children}
				</div>
				<Ripple ripples={ripples} onRippleDone={removeRipple} />
			</div>
		);
	},
);

CarouselItem.displayName = "CarouselItem";
